/**
 * AuthContext & AuthProvider
 *
 * Manages authentication state and provides auth operations to the app
 * - Loads session from storage on mount
 * - Listens for storage changes (logout in other tabs)
 * - Provides login/logout methods
 * - Exposes current user state and flags
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { AuthSession } from './authTypes';
import {
  loginWithPassword,
  exchangeGoogleApple,
  logout as authServiceLogout,
  refreshTokenSingleFlight,
  getCurrentSession,
} from './authService';
import { loadPersistedTokens } from './authStorage';

/**
 * Context interface
 */
interface AuthContextType {
  // State
  session: AuthSession | null;
  isLoading: boolean;

  // Derived state
  isAuthenticated: boolean;
  userId: string | null;
  accessToken: string | null;
  emailConfirmed: boolean;
  profileConfirmed: boolean;

  // Methods
  loginWithPassword: (email: string, password: string, rememberMe?: boolean) => Promise<AuthSession>;
  setSession: (session: AuthSession | null) => void;
  exchangeGoogleApple: (
    idToken: string,
    provider: 'google' | 'apple',
    nonceSha256?: string
  ) => Promise<AuthSession>;
  refresh: () => Promise<AuthSession>;
  logout: () => Promise<void>;
}

/**
 * Create context with undefined as initial value
 */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Props for AuthProvider
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider component
 * Wrap your app root with this to enable auth functionality
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * On mount: load session from storage and set up event listeners
   */
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Load stored session
        const stored = loadPersistedTokens();

        if (stored) {
          setSession(stored);
          // Optionally refresh if token is stale
          try {
            await refreshTokenSingleFlight();
            const refreshed = getCurrentSession();
            if (refreshed) {
              setSession(refreshed);
            }
          } catch (error) {
            console.warn('Failed to refresh on init:', error);
            // Continue with stored session anyway
          }
        } else {
          console.warn('No session found in storage');
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        // Session remains null, user will be redirected to login
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  /**
   * Listen for storage events (logout in other tabs)
   */
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (
        event.key === 'circlebe.refreshToken' ||
        event.key === 'circlebe.expiresAt'
      ) {
        if (event.newValue === null) {
          // Refresh token was removed; logout this tab
          setSession(null);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  /**
   * Login with email and password
   */
  const handleLoginWithPassword = useCallback(
    async (email: string, password: string, rememberMe?: boolean): Promise<AuthSession> => {
      const newSession = await loginWithPassword(email, password, rememberMe ?? false);
      setSession(newSession);
      return newSession;
    },
    []
  );

  /**
   * Exchange OAuth token (Google/Apple)
   */
  const handleExchangeGoogleApple = useCallback(
    async (
      idToken: string,
      provider: 'google' | 'apple',
      nonceSha256?: string
    ): Promise<AuthSession> => {
      const newSession = await exchangeGoogleApple(
        idToken,
        provider,
        nonceSha256
      );
      setSession(newSession);
      return newSession;
    },
    []
  );

  /**
   * Refresh access token
   */
  const handleRefresh = useCallback(async (): Promise<AuthSession> => {
    const newSession = await refreshTokenSingleFlight();
    setSession(newSession);
    return newSession;
  }, []);

  /**
   * Logout
   */
  const handleLogout = useCallback(async (): Promise<void> => {
    await authServiceLogout();
    setSession(null);
  }, []);

  const value: AuthContextType = {
    // State
    session,
    isLoading,

    // Derived
    isAuthenticated: session !== null,
    userId: session?.userId ?? null,
    accessToken: session?.accessToken ?? null,
    emailConfirmed: session?.emailConfirmed ?? false,
    profileConfirmed: session?.profileConfirmed ?? false,

    // Methods
    loginWithPassword: handleLoginWithPassword,
    setSession,
    exchangeGoogleApple: handleExchangeGoogleApple,
    refresh: handleRefresh,
    logout: handleLogout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access auth context
 * Throws error if used outside of AuthProvider
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
